/**
 * Ctrl/Cmd+K palette (BUILD-PLAN 3.6). Entities and verbs in one box:
 * live search providers (patients first), then registered commands ranked
 * by text match, synonyms, frequency and recency. Privacy mode shows
 * initials for patient rows. "Ask <product>" is the last row.
 */
import { PRODUCT } from '@velora/contracts/product';
import { CommandPalette, type PaletteGroup, type PaletteItem } from '@velora/ui';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Icon } from '../icons.js';
import { readJson, writeJson } from '../lib/storage.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import { PatientOfficeTag } from '../office/PatientOfficeTag.js';
import { usePrivacy } from '../privacy/privacy.js';
import { canInScope } from '../session/access.js';
import { useSession } from '../session/session.js';
import { shortcutLabels } from '../shortcuts/keys.js';
import { isMacPlatform } from '../shortcuts/platform.js';
import { aliasMatch, rank, recordUse, type UsageStats } from './rank.js';
import {
  commandRegistry,
  searchRegistry,
  type SearchResult,
  type ShellCommand,
} from './registry.js';

export type PaletteMode = 'all' | 'patients';

const SEARCH_DEBOUNCE_MS = 120;
const GROUP_ORDER = ['Navigate', 'Actions', 'Offices', 'Preferences', 'Help'];

function usageKey(practiceId: string, userId: string) {
  return `velora.palette.usage.${practiceId}.${userId}`;
}

interface ProviderState {
  id: string;
  heading: string;
  order: number;
  results: readonly SearchResult[];
}

export function PaletteHost({
  open,
  mode,
  onOpenChange,
  onAsk,
}: {
  open: boolean;
  mode: PaletteMode;
  onOpenChange: (open: boolean) => void;
  onAsk?: (query: string) => void;
}) {
  const session = useSession();
  const { param, locationIds } = useOfficeScope();
  const privacy = usePrivacy();
  const commands = useSyncExternalStore(
    commandRegistry.subscribe,
    commandRegistry.get,
    commandRegistry.get,
  );
  const providers = useSyncExternalStore(
    searchRegistry.subscribe,
    searchRegistry.get,
    searchRegistry.get,
  );
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<ProviderState[]>([]);
  const [loading, setLoading] = useState(false);
  const uKey = usageKey(session.practice.id, session.user.id);
  const [usage, setUsage] = useState<UsageStats>(() => readJson<UsageStats>(uKey, {}));
  const isMac = isMacPlatform();
  const requestSeq = useRef(0);

  // Fresh box every time it opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setFound([]);
    }
  }, [open, mode]);

  const allowed = useMemo(
    () => commands.filter((c) => !c.permission || canInScope(session, c.permission, locationIds)),
    [commands, session, locationIds],
  );

  const activeProviders = useMemo(
    () =>
      providers
        .filter((p) => (mode === 'patients' ? p.patients !== false : true))
        .filter((p) => !p.permission || canInScope(session, p.permission, locationIds))
        .sort((a, b) => (a.order ?? 50) - (b.order ?? 50)),
    [providers, mode, session, locationIds],
  );

  // Live search, debounced and cancelled on every keystroke.
  useEffect(() => {
    const q = query.trim();
    const runnable = activeProviders.filter((p) => q.length >= (p.minLength ?? 2));
    if (!open || runnable.length === 0) {
      setFound([]);
      setLoading(false);
      return undefined;
    }
    const ctrl = new AbortController();
    const seq = ++requestSeq.current;
    setLoading(true);
    const t = setTimeout(() => {
      void Promise.all(
        runnable.map(async (p) => {
          try {
            const results = await p.search(q, { signal: ctrl.signal, office: param });
            return { id: p.id, heading: p.heading, order: p.order ?? 50, results };
          } catch {
            return { id: p.id, heading: p.heading, order: p.order ?? 50, results: [] };
          }
        }),
      ).then((states) => {
        if (seq !== requestSeq.current || ctrl.signal.aborted) return;
        setFound(states);
        setLoading(false);
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query, activeProviders, open, param]);

  const remember = (id: string) => {
    setUsage((prev) => {
      const next = recordUse(prev, id, Date.now());
      writeJson(uKey, next);
      return next;
    });
  };

  const groups = useMemo<PaletteGroup[]>(() => {
    const out: PaletteGroup[] = [];
    for (const p of [...found].sort((a, b) => a.order - b.order)) {
      if (p.results.length === 0) continue;
      out.push({
        id: `search:${p.id}`,
        heading: p.heading,
        items: p.results.map<PaletteItem>((r) => ({
          id: `${p.id}:${r.id}`,
          label: r.phi && privacy.on ? privacy.mask(r.label) : r.label,
          ...(r.hint ? { hint: r.phi && privacy.on ? 'Hidden in privacy mode' : r.hint } : {}),
          icon: <Icon name={r.icon ?? 'user-round'} size={15} />,
          ...(r.office || r.adornment
            ? {
                badge: (
                  <>
                    {r.adornment}
                    {r.office ? <PatientOfficeTag hit={r.office} /> : null}
                  </>
                ),
              }
            : {}),
          onSelect: r.run,
        })),
      });
    }
    if (mode === 'patients') return out;

    const now = Date.now();
    const toItem = (c: ShellCommand): PaletteItem => ({
      id: c.id,
      label: c.title,
      ...(c.hint ? { hint: c.hint } : {}),
      ...(c.keywords ? { keywords: c.keywords } : {}),
      icon: <Icon name={c.icon ?? 'command'} size={15} />,
      ...(c.shortcut ? { shortcut: shortcutLabels(c.shortcut, isMac) } : {}),
      onSelect: () => {
        remember(c.id);
        c.run();
      },
    });

    const q = query.trim();
    if (!q) {
      const recent = allowed
        .filter((c) => usage[c.id])
        .sort((a, b) => (usage[b.id]?.last ?? 0) - (usage[a.id]?.last ?? 0))
        .slice(0, 4);
      if (recent.length > 0) {
        out.push({ id: 'recent', heading: 'Recent', items: recent.map(toItem) });
      }
      const recentIds = new Set(recent.map((c) => c.id));
      const byGroup = new Map<string, ShellCommand[]>();
      for (const c of allowed) {
        if (recentIds.has(c.id)) continue;
        const list = byGroup.get(c.group) ?? [];
        list.push(c);
        byGroup.set(c.group, list);
      }
      const names = [...byGroup.keys()].sort(
        (a, b) => (GROUP_ORDER.indexOf(a) + 1 || 99) - (GROUP_ORDER.indexOf(b) + 1 || 99),
      );
      for (const name of names) {
        const items = (byGroup.get(name) ?? []).sort(
          (a, b) => (b.weight ?? 0) - (a.weight ?? 0) || a.title.localeCompare(b.title),
        );
        out.push({ id: `g:${name}`, heading: name, items: items.map(toItem) });
      }
      return out;
    }

    const ranked = rank(
      q,
      allowed.map((c) => ({ ...c, label: c.title })),
      usage,
      now,
    ).slice(0, 12);
    if (ranked.length > 0) {
      // A command found through an old-PMS word says so ("Dentrix: Continuing Care").
      const explained = ranked.map((c) => {
        const alias = aliasMatch(q, c);
        return alias ? { ...c, hint: `${alias.skin}: ${alias.phrase}` } : c;
      });
      const commands = { id: 'commands', heading: 'Commands', items: explained.map(toItem) };
      // Typing a page's exact name ("schedule") puts it first, above search
      // results that merely mention the word (familiar-nav 2026-10-29).
      if (explained[0]?.title.toLowerCase() === q.toLowerCase()) out.unshift(commands);
      else out.push(commands);
    }
    return out;
    // `remember` only touches state setters and storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found, mode, query, allowed, usage, privacy.on, isMac]);

  const assistantName = PRODUCT.shortName;
  return (
    <CommandPalette
      open={open}
      onOpenChange={onOpenChange}
      groups={groups}
      query={query}
      onQueryChange={setQuery}
      filter={false}
      loading={loading}
      assistantName={assistantName}
      placeholder={
        mode === 'patients'
          ? 'Search patients by name, chart number or birthdate'
          : 'Search patients, jump to a page, or type a command'
      }
      {...(onAsk && mode === 'all' ? { onAsk } : {})}
    />
  );
}
